<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';

defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add']);

const { t } = useI18n();

const noteContent = ref('');
const isExpanded = ref(false);

function handleSubmit() {
  if (!noteContent.value.trim()) return;
  emit('add', noteContent.value.trim());
  noteContent.value = '';
  isExpanded.value = false;
}

function handleCancel() {
  noteContent.value = '';
  isExpanded.value = false;
}

function handleFocus() {
  isExpanded.value = true;
}
</script>

<template>
  <div class="add-note">
    <div class="add-note__input-wrapper">
      <fluent-icon icon="document" size="16" class="add-note__icon" />
      <textarea
        v-model="noteContent"
        :placeholder="t('CRM.ACTIVITY.ADD_NOTE_PLACEHOLDER')"
        class="add-note__input"
        :rows="isExpanded ? 3 : 1"
        :disabled="isLoading"
        @focus="handleFocus"
      />
    </div>

    <div v-if="isExpanded" class="add-note__actions">
      <button
        class="add-note__btn add-note__btn--cancel"
        :disabled="isLoading"
        @click="handleCancel"
      >
        {{ t('CRM.ACTIVITY.CANCEL') }}
      </button>
      <button
        class="add-note__btn add-note__btn--submit"
        :disabled="!noteContent.trim() || isLoading"
        @click="handleSubmit"
      >
        <Spinner v-if="isLoading" size="small" />
        <span v-else>{{ t('CRM.ACTIVITY.ADD_NOTE') }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.add-note {
  @apply mb-4;
}

.add-note__input-wrapper {
  @apply flex items-start gap-2 p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800;
}

.add-note__icon {
  @apply text-slate-400 mt-1;
}

.add-note__input {
  @apply flex-1 bg-transparent text-sm text-slate-700 dark:text-slate-200 resize-none outline-none;
}

.add-note__input::placeholder {
  @apply text-slate-400 dark:text-slate-500;
}

.add-note__actions {
  @apply flex justify-end gap-2 mt-2;
}

.add-note__btn {
  @apply px-3 py-1.5 text-sm font-medium rounded-md transition-colors;
}

.add-note__btn--cancel {
  @apply text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700;
}

.add-note__btn--submit {
  @apply bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1;
}
</style>
